import { Box, Tab, Tabs, Typography, useTheme } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import TaskMessages from 'components/Comments/TaskMessages'
import TaskTimeline from 'components/TeamTasks/TaskTimeline'
import React, { FC, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { BatchUpdateItem, OrganizationTableItem, Subtask, Task } from 'types'
import AutoChecklist from './AutoChecklist'
import TaskDescription from './TaskDescription'
import TaskDetails from './TaskDetails'
import TooltipIcon from './TooltipIcon'

type Item = { id: string; label: string; done?: boolean }

interface InnerTaskRoutingProps {
  task: Task
  organization: OrganizationTableItem
  subtasks: Subtask[]
  isTaskLoading: boolean
  onNavigate: (route: string) => void
  onChange: (field: string, newValue: any) => void
  onSaveAll: () => void
  onSaveChecklist: () => void
  onStartEdit: () => void
  onStopEdit: () => void
  isUpdating: boolean
  addtlNotes: string
  expanded: boolean
  items?: { id: string; label: string; done?: boolean }[]
  editing?: boolean
  onToggle?: (item: { id: string; label: string; done?: boolean }) => void
  registerTypeUpdateBuilder?: (fn: () => BatchUpdateItem[]) => void

  // 🔽 NEW: tiny, stable navigation API
  selectedRowId: string | null
  getNeighbors: (id: string) => { prevId: string | null; nextId: string | null; index: number; total: number }
  onPrev?: () => void
  onNext?: () => void
  navState?: { index: number; total: number; hasPrev: boolean; hasNext: boolean }
  isCommentOpen: boolean
  setIsCommentOpen: React.Dispatch<React.SetStateAction<boolean>>
  // Details props
  localDetails?: string
}

function a11yProps(index: number) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  }
}

const Panel = ({ children, value, index }) => {
  if (value !== index) return null
  return (
    <Box role="tabpanel" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', display: 'flex', width: '100%' }}>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>{children}</Box>
    </Box>
  )
}

const TabLabel = ({ label }: { label: string }) => <Typography sx={{ fontSize: 10 }}>{label}</Typography>

const ACTIONS = { edit: 'action-edit', save: 'action-save', cancel: 'action-cancel', saveChk: 'action-save-checklist' }
const showsActions = (v: number) => v === 0 || v === 2 || v === 3
const TAB_SEGMENTS = ['main', 'messages', 'checklist', 'description', 'timeline'] as const
const getSegmentFromTab = (value: number) => TAB_SEGMENTS[value] ?? 'main'

// ------------------------
// Component (logic section)
// ------------------------
const InnerTaskRouting: FC<InnerTaskRoutingProps> = ({
  task,
  organization,
  subtasks,
  isTaskLoading,
  onNavigate,
  onChange,
  onSaveAll,
  onSaveChecklist,
  onStartEdit,
  onStopEdit,
  isUpdating,
  addtlNotes,
  expanded,
  items,
  editing,
  onToggle,
  registerTypeUpdateBuilder,

  // 🔽 NEW nav props
  selectedRowId,
  getNeighbors,
  onPrev,
  onNext,
  navState,
  isCommentOpen,
  setIsCommentOpen,
  // Details props
  localDetails,
}) => {
  const { PK, SK } = useParams()
  const [value, setValue] = useState(0)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()

  // --- Tab state <-> URL sync ---
  const handleChange = (event, newValue) => {
    if (typeof newValue === 'string' && newValue.startsWith('action-')) {
      event.preventDefault()
      return
    }
    setValue(newValue)
    const baseRoute = location.pathname.split('/')[1]
    const seg = getSegmentFromTab(newValue)
    navigate(`/${baseRoute}/${PK}/${SK}/${seg}`)
  }

  useEffect(() => {
    if (location.pathname.includes('/messages')) setValue(1)
    else if (location.pathname.includes('/checklist')) setValue(2)
    else if (location.pathname.includes('/description')) setValue(3)
    else if (location.pathname.includes('/timeline')) setValue(4)
    else setValue(0)
  }, [location.pathname])

  const isChecklistTab = value === 2
  const canEditThisTask = task?.isCreative || task?.type === 'website' || value === 3

  // --- Neighbors (tiny + stable) ---
  const { prevId, nextId, index, total } = React.useMemo(
    () => (selectedRowId ? getNeighbors(selectedRowId) : { prevId: null, nextId: null, index: -1, total: 0 }),
    [selectedRowId, getNeighbors]
  )
  const hasPrev = navState?.hasPrev ?? !!prevId
  const hasNext = navState?.hasNext ?? !!nextId

  const getCurrentTabSegment = () => {
    const segs = ['main', 'messages', 'checklist', 'description', 'timeline'] as const
    return segs.find(s => location.pathname.includes('/' + s)) ?? 'main'
  }
  const splitRowId = (rowId: string) => {
    const [pk, sk] = rowId.split('::')
    return { pk, sk }
  }
  const baseFromPath = () => (location.pathname.startsWith('/projects') ? 'projects' : 'teamtasks')

  // Use parent-provided onPrev/onNext if passed; otherwise route locally.
  const goPrev = React.useCallback(() => {
    if (onPrev) return onPrev()
    if (!prevId) return
    const { pk, sk } = splitRowId(prevId)
    navigate(`/${baseFromPath()}/${pk}/${sk}/${getCurrentTabSegment()}`)
  }, [onPrev, prevId, navigate])

  const goNext = React.useCallback(() => {
    if (onNext) return onNext()
    if (!nextId) return
    const { pk, sk } = splitRowId(nextId)
    navigate(`/${baseFromPath()}/${pk}/${sk}/${getCurrentTabSegment()}`)
  }, [onNext, nextId, navigate])

  // --- Arrow key shortcuts (← / →) ---
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && hasPrev) {
        e.preventDefault()
        goPrev()
      }
      if (e.key === 'ArrowRight' && hasNext) {
        e.preventDefault()
        goNext()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [hasPrev, hasNext, goPrev, goNext])

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', height: '100dvh', width: '100%' }}>
      <Tabs
        orientation="vertical"
        variant="scrollable"
        value={value}
        onChange={handleChange}
        aria-label="Vertical tabs example"
        sx={{ borderRight: 1, borderColor: 'divider' }}
      >
        <Tab icon={<TooltipIcon title={'Details'} />} label={<TabLabel label={'Details'} />} {...a11yProps(0)} />
        <Tab
          icon={<TooltipIcon title={'Comments'} />}
          disabled={editing}
          label={<TabLabel label={'Comments'} />}
          {...a11yProps(1)}
        />
        <Tab
          icon={<TooltipIcon title={'Checklist'} />}
          disabled={!items?.length}
          label={<TabLabel label={'Checklist'} />}
          {...a11yProps(2)}
        />
        <Tab
          icon={<TooltipIcon title={'Description'} />}
          label={<TabLabel label={'Description'} />}
          {...a11yProps(3)}
        />
        <Tab
          icon={<TooltipIcon title={'Timeline'} />}
          disabled={editing}
          label={<TabLabel label={'Timeline'} />}
          {...a11yProps(4)}
        />
        {/* separator */}
        <Tab
          value="action-separator"
          disabled
          disableRipple
          label=""
          onMouseDown={e => e.preventDefault()}
          sx={{
            minHeight: 8,
            p: 0,
            cursor: 'default',
            '&.Mui-disabled': { opacity: 1 },
            borderTop: theme => `1px solid ${theme.palette.divider}`,
            mx: 0.5,
          }}
        />

        {/* EDIT */}
        {showsActions(value) && task && canEditThisTask && !editing && (
          <Tab
            value={ACTIONS.edit}
            icon={<TooltipIcon title="Edit Task" />}
            label={<TabLabel label="Edit" />}
            disableRipple
            onMouseDown={e => e.preventDefault()}
            onClick={e => {
              e.stopPropagation()
              onStartEdit()
            }}
            sx={{ '& .MuiTab-iconWrapper': { m: 0, p: 0 } }}
          />
        )}
        {/* CANCEL */}
        {showsActions(value) && task && canEditThisTask && editing && (
          <Tab
            value={ACTIONS.cancel}
            icon={<TooltipIcon title="Cancel" />}
            label={<TabLabel label="Cancel" />}
            disableRipple
            onMouseDown={e => e.preventDefault()}
            onClick={e => {
              e.stopPropagation()
              onStopEdit()
            }}
            sx={{ '& .MuiTab-iconWrapper': { m: 0, p: 0 } }}
          />
        )}
        {/* SAVE (non-checklist tabs) */}
        {showsActions(value) && task && canEditThisTask && editing && !isChecklistTab && (
          <Tab
            value={ACTIONS.save}
            icon={<TooltipIcon title="Save" />}
            label={<TabLabel label="Save" />}
            disableRipple
            onMouseDown={e => e.preventDefault()}
            onClick={e => {
              e.stopPropagation()
              onSaveAll()
            }}
            sx={{ '& .MuiTab-iconWrapper': { m: 0, p: 0 } }}
          />
        )}

        {/* SAVE CHECKLIST (checklist tab only) */}
        {showsActions(value) && task && canEditThisTask && editing && isChecklistTab && (
          <Tab
            value={ACTIONS.saveChk}
            icon={<TooltipIcon title="Save Chklist" />}
            label={<TabLabel label="Save Chklist" />}
            disableRipple
            onMouseDown={e => e.preventDefault()}
            onClick={e => {
              e.stopPropagation()
              onSaveChecklist()
            }}
            sx={{ '& .MuiTab-iconWrapper': { m: 0, p: 0 } }}
          />
        )}
        <Tab
          value={null}
          icon={<TooltipIcon title="Previous Task" />}
          label={<TabLabel label="Previous Task" />}
          onMouseDown={e => e.preventDefault()}
          onClick={e => {
            e.stopPropagation()
            goPrev() // ← use the memoized neighbor nav
          }}
          disabled={!hasPrev} // ← from neighbors
        />

        <Tab
          value={null}
          icon={<TooltipIcon title="Next Task" />}
          label={<TabLabel label="Next Task" />}
          onMouseDown={e => e.preventDefault()}
          onClick={e => {
            e.stopPropagation()
            goNext() // ← use the memoized neighbor nav
          }}
          disabled={!hasNext} // ← from neighbors
        />
      </Tabs>

      <Panel value={value} index={0}>
        <TaskDetails
          task={task}
          organization={organization}
          isTaskLoading={isTaskLoading}
          onChange={onChange}
          addtlNotes={addtlNotes}
          expanded={expanded}
          editing={editing}
          onNavigate={onNavigate}
          registerTypeUpdateBuilder={registerTypeUpdateBuilder}
        />
      </Panel>
      <Panel value={value} index={1}>
        <TaskMessages
          expanded={expanded}
          context={'teamtasks'}
          subtasks={subtasks}
          isCommentOpen={isCommentOpen}
          setIsCommentOpen={setIsCommentOpen}
        />
      </Panel>
      <Panel value={value} index={2}>
        <AutoChecklist items={items} editing={editing} onToggle={onToggle} />
      </Panel>
      <Panel value={value} index={3}>
        <TaskDescription
          addtlNotes={addtlNotes}
          taskAddtlNotes={task?.addtlNotes || task?.description}
          onChange={onChange}
          localDetails={localDetails}
          taskDetails={task?.details}
          editing={editing}
        />
      </Panel>
      <Panel value={value} index={4}>
        <TaskTimeline expanded={expanded} />
      </Panel>
    </Box>
  )
}
export default InnerTaskRouting
